/**
 * SOLID Review
 * S — Strong. 48 lines, two pure functions (resolveAwaitedPlayerId, resolveBlockingSuspendedOpponentId)
 *     that answer one question each: who is the phase waiting on, and is that person a suspended opponent
 *     blocking the table. No React, no side effects, no unrelated concerns mixed in.
 * O — Good. The phase→awaited-player switch would need a new case if GamePhase grows a value that awaits a
 *     specific player, but that is inherent to modeling a fixed enum, not a design flaw; both functions
 *     take their inputs as plain parameters so no existing call site changes.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two narrow, single-purpose exported functions; resolveBlockingSuspendedOpponentId is itself
 *     built from resolveAwaitedPlayerId rather than duplicating its logic.
 * D — Strong. Depends only on the GamePhase enum from the domain layer; no store, port, or infrastructure
 *     imports at all.
 */
import { GamePhase } from '../uml-components/primitives';

// MOVED from application/use-cases/resolveBlockingSuspension.ts (frontend strict-ports plan, FE1,
// 2026-10-03). It is a game rule — whose move the table waits on — and the table decisions derive
// `blockingOpponentId` with it; the Domain cannot import the Application, so the rule moved down.

/**
 * The player whose action the current phase is actually waiting on, or null when
 * the phase awaits no specific player's input (REVEALING, lobby/idle, round
 * transitions). In Meyer the awaited actor is the roller while they roll / peek /
 * claim, and the challenger once a claim is on the table (DECIDING).
 */
export function resolveAwaitedPlayerId(
  phase: GamePhase,
  activeRollerId: string | null,
  challengerId: string | null,
): string | null {
  switch (phase) {
    case GamePhase.DECIDING:
      return challengerId;
    case GamePhase.ROLLING:
    case GamePhase.PEEKING:
    case GamePhase.CLAIMING:
      return activeRollerId;
    default:
      return null;
  }
}

/**
 * The id of the suspended opponent who is *currently blocking the table* — i.e.
 * the awaited player, if they happen to be suspended and aren't the local player.
 *
 * Returns null when the player the game is waiting on is NOT suspended, so an
 * incidental drop by a passive player (common in 3-4 player games, or on a single
 * device where only one tab can be foreground) never freezes everyone else — they
 * simply show as SUSPENDED in the roster while play continues. The server already
 * auto-advances when the awaited player is the one that drops (SKIP_ROLLER for a
 * suspended roller, AUTO_BELIEVE for a suspended challenger), after which that
 * player is no longer awaited and this returns null again.
 */
export function resolveBlockingSuspendedOpponentId(
  phase: GamePhase,
  activeRollerId: string | null,
  challengerId: string | null,
  localPlayerId: string | null,
  suspendedPlayerIds: readonly string[],
): string | null {
  const awaited = resolveAwaitedPlayerId(phase, activeRollerId, challengerId);
  if (!awaited || awaited === localPlayerId) return null;
  return suspendedPlayerIds.includes(awaited) ? awaited : null;
}
